import React from 'react';
import ModernCTA from '../../components/ModernCTA';

const AgriTechAIPage = () => {
  return (
    <div className="agritech-page" style={{ marginTop: '110px' }}>
      {/* Animated Background Elements */}
      <div className="animated-bg-element leaf"></div>
      <div className="animated-bg-element water-drop"></div>
      <div className="animated-bg-element sun"></div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            <span className="gradient-text">AgriTech AI</span> for Smarter Farming
          </h1>
          <p className="hero-subtitle">
            Precision farming solutions powered by drone imagery analysis and automated irrigation systems to maximize your yield
          </p>
          <div className="hero-features">
            <span className="feature-bubble">
              <span className="bubble-icon">🛰️</span> Drone Analytics
            </span>
            <span className="feature-bubble">
              <span className="bubble-icon">💧</span> Smart Irrigation
            </span>
            <span className="feature-bubble">
              <span className="bubble-icon">🌱</span> Crop Health Monitoring
            </span>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="solutions-section">
        <div className="section-header">
          <h2 className="section-title">Precision <span className="gradient-text">Farming Solutions</span></h2>
          <p className="section-description">
            Harness the power of AI and IoT to optimize every aspect of your agricultural operations
          </p>
        </div>
        
        <div className="solution-cards">
          <div className="solution-card">
            <div className="card-icon">🛰️</div>
            <h3 className="card-title">Drone Imagery Analysis</h3>
            <p className="card-desc">
              High-resolution aerial monitoring with NDVI, thermal imaging, and multispectral analysis for complete field visibility.
            </p>
            <ul className="card-features">
              <li><span className="feature-highlight">Crop Health:</span> Early disease detection</li>
              <li><span className="feature-highlight">Growth Tracking:</span> Vegetation index mapping</li>
              <li><span className="feature-highlight">Yield Prediction:</span> AI-powered forecasts</li>
              <li><span className="feature-highlight">Zoning:</span> Variable-rate application maps</li>
            </ul>
          </div>
          
          <div className="solution-card">
            <div className="card-icon">🚜</div>
            <h3 className="card-title">Automated Irrigation</h3>
            <p className="card-desc">
              Smart water management systems that respond to soil conditions and weather forecasts in real-time.
            </p>
            <ul className="card-features">
              <li><span className="feature-highlight">Soil Sensors:</span> Moisture, temperature, nutrient levels</li>
              <li><span className="feature-highlight">Weather Integration:</span> Predictive watering schedules</li>
              <li><span className="feature-highlight">Remote Control:</span> Mobile app management</li>
              <li><span className="feature-highlight">Water Savings:</span> Up to 40% reduction</li>
            </ul>
          </div>
          
          <div className="solution-card">
            <div className="card-icon">📱</div>
            <h3 className="card-title">Farm Management AI</h3>
            <p className="card-desc">
              Comprehensive digital platform integrating all farm data with actionable insights.
            </p>
            <ul className="card-features">
              <li><span className="feature-highlight">Task Automation:</span> Planting/harvest alerts</li>
              <li><span className="feature-highlight">Inventory Tracking:</span> Seed to harvest</li>
              <li><span className="feature-highlight">Pest Alerts:</span> Early warning system</li>
              <li><span className="feature-highlight">Equipment Monitoring:</span> Predictive maintenance</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="tech-section">
        <div className="section-header">
          <h2 className="section-title">Our <span className="gradient-text">AgriTech Stack</span></h2>
          <p className="section-description">
            Cutting-edge technologies powering your agricultural transformation
          </p>
        </div>
        
        <div className="tech-grid">
          <div className="tech-item">
            <div className="tech-icon">🌐</div>
            <h3 className="tech-title">Remote Sensing</h3>
            <p className="tech-desc">
              Drones, satellites, IoT sensors with multispectral, thermal, and hyperspectral imaging
            </p>
          </div>
          
          <div className="tech-item">
            <div className="tech-icon">🧠</div>
            <h3 className="tech-title">AI/ML Models</h3>
            <p className="tech-desc">
              Computer vision, predictive analytics, time-series forecasting, anomaly detection
            </p>
          </div>
          
          <div className="tech-item">
            <div className="tech-icon">💧</div>
            <h3 className="tech-title">Irrigation Tech</h3>
            <p className="tech-desc">
              Smart valves, moisture sensors, weather stations, automated control systems
            </p>
          </div>
          
          <div className="tech-item">
            <div className="tech-icon">📊</div>
            <h3 className="tech-title">Data Platform</h3>
            <p className="tech-desc">
              Cloud storage, GIS integration, farmer-friendly dashboards, mobile apps
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-header">
          <h2 className="section-title">Farm <span className="gradient-text">Transformation</span></h2>
          <p className="section-description">
            Measurable improvements our AgriTech solutions deliver
          </p>
        </div>
        
        <div className="benefits-grid">
          <div className="benefit-item">
            <div className="benefit-value">+25%</div>
            <h3 className="benefit-title">Yield Increase</h3>
            <p className="benefit-desc">
              Optimized planting density and nutrient management boosts production
            </p>
          </div>
          
          <div className="benefit-item">
            <div className="benefit-value">-40%</div>
            <h3 className="benefit-title">Water Savings</h3>
            <p className="benefit-desc">
              Precision irrigation reduces waste while maintaining crop health
            </p>
          </div>
          
          <div className="benefit-item">
            <div className="benefit-value">-30%</div>
            <h3 className="benefit-title">Input Costs</h3>
            <p className="benefit-desc">
              Targeted application of fertilizers and pesticides cuts expenses
            </p>
          </div>
          
          <div className="benefit-item">
            <div className="benefit-value">24/7</div>
            <h3 className="benefit-title">Field Monitoring</h3>
            <p className="benefit-desc">
              Constant vigilance detects issues before they impact yield
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <ModernCTA />
      </section>

      {/* CSS Styles */}
      <style jsx>{`
        .agritech-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #e2e8f0;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          min-height: 100vh;
          position: relative;
          overflow-x: hidden;
        }
        
        /* Nature-themed Animated Background Elements */
        .animated-bg-element {
          position: absolute;
          border-radius: 50%;
          filter: blur(60px);
          opacity: 0.15;
          animation: float 15s infinite ease-in-out;
          z-index: 1;
        }
        
        .leaf {
          width: 300px;
          height: 300px;
          background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
          top: 10%;
          left: -100px;
          animation-delay: 0s;
        }
        
        .water-drop {
          width: 400px;
          height: 400px;
          background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
          bottom: 10%;
          right: -150px;
          animation-delay: 3s;
        }
        
        .sun {
          width: 250px;
          height: 250px;
          background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
          top: 50%;
          left: 30%;
          animation-delay: 6s;
        }
        
        @keyframes float {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(20px, 20px); }
          50% { transform: translate(-20px, 10px); }
          75% { transform: translate(10px, -20px); }
        }
        
        /* Hero Section */
        .hero-section {
          padding: 6rem 2rem 8rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        
        .hero-content {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }
        
        .hero-title {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1.5rem;
          letter-spacing: -0.05em;
          animation: fadeInUp 1s ease;
        }
        
        .hero-subtitle {
          font-size: 1.25rem;
          line-height: 1.6;
          max-width: 700px;
          margin: 0 auto 2.5rem;
          opacity: 0.9;
          animation: fadeInUp 1s ease 0.2s forwards;
          opacity: 0;
        }
        
        .hero-features {
          display: flex;
          justify-content: center;
          gap: 1rem;
          flex-wrap: wrap;
          animation: fadeInUp 1s ease 0.4s forwards;
          opacity: 0;
        }
        
        .feature-bubble {
          background: rgba(74, 222, 128, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(34, 197, 94, 0.2);
          padding: 0.75rem 1.5rem;
          border-radius: 2rem;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .feature-bubble:hover {
          background: rgba(74, 222, 128, 0.2);
          transform: translateY(-3px) scale(1.05);
          box-shadow: 0 5px 15px -5px rgba(74, 222, 128, 0.3);
        }
        
        .bubble-icon {
          font-size: 1.1rem;
        }
        
        /* Gradient Text */
        .gradient-text {
          background: linear-gradient(90deg, #4ade80 0%, #22d3ee 50%, #60a5fa 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: gradientShift 8s ease infinite;
          background-size: 200% 200%;
        }
        
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Sections Common Styles */
        section {
          padding: 5rem 2rem;
          position: relative;
          z-index: 2;
        }
        
        .section-header {
          max-width: 800px;
          margin: 0 auto 4rem;
          text-align: center;
        }
        
        .section-title {
          font-size: 2.5rem;
          margin-bottom: 1.5rem;
          font-weight: 700;
          letter-spacing: -0.025em;
        }
        
        .section-description {
          color: #94a3b8;
          font-size: 1.1rem;
          line-height: 1.6;
        }
        
        /* Solutions Section */
        .solution-cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2.5rem;
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .solution-card {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(148, 163, 184, 0.1);
          border-radius: 1.25rem;
          padding: 2.5rem 2rem;
          transition: all 0.4s ease;
          backdrop-filter: blur(10px);
          transform-style: preserve-3d;
          perspective: 1000px;
        }
        
        .solution-card:hover {
          transform: translateY(-10px) rotateX(5deg);
          border-color: rgba(74, 222, 128, 0.3);
          box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.3);
          background: rgba(15, 23, 42, 0.7);
        }
        
        .card-icon {
          font-size: 2.5rem;
          margin-bottom: 1.5rem;
          display: inline-block;
          transition: transform 0.3s ease;
        }
        
        .solution-card:hover .card-icon {
          transform: scale(1.2) rotate(10deg);
        }
        
        .card-title {
          font-size: 1.5rem;
          margin-bottom: 1.25rem;
          font-weight: 600;
          position: relative;
          display: inline-block;
        }
        
        .card-title:after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #4ade80 0%, #22d3ee 100%);
          transition: width 0.3s ease;
        }
        
        .solution-card:hover .card-title:after {
          width: 100%;
        }
        
        .card-desc {
          color: #94a3b8;
          margin-bottom: 1.5rem;
          line-height: 1.6;
          min-height: 4.5rem;
        }
        
        .card-features {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .card-features li {
          position: relative;
          padding-left: 1.75rem;
          margin-bottom: 0.85rem;
          color: #cbd5e1;
          transition: all 0.3s ease;
        }
        
        .card-features li:before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.6em;
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 50%;
          background: #4ade80;
          transform: scale(0);
          transition: transform 0.3s ease;
        }
        
        .solution-card:hover .card-features li:before {
          transform: scale(1);
        }
        
        .feature-highlight {
          font-weight: 600;
          color: #4ade80;
        }
        
        /* Technology Section */
        .tech-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .tech-item {
          background: rgba(15, 23, 42, 0.4);
          border-radius: 1rem;
          padding: 2rem 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
          border: 1px solid rgba(148, 163, 184, 0.05);
        }
        
        .tech-item:hover {
          background: rgba(15, 23, 42, 0.7);
          transform: translateY(-5px);
          border-color: rgba(74, 222, 128, 0.2);
          box-shadow: 0 10px 20px -10px rgba(74, 222, 128, 0.1);
        }
        
        .tech-icon {
          font-size: 2rem;
          margin-bottom: 1.5rem;
          display: inline-block;
          transition: transform 0.3s ease;
        }
        
        .tech-item:hover .tech-icon {
          transform: rotate(15deg) scale(1.2);
        }
        
        .tech-title {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .tech-desc {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
        }
        
        /* Benefits Section */
        .benefits-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          max-width: 1000px;
          margin: 0 auto;
        }
        
        .benefit-item {
          background: rgba(15, 23, 42, 0.5);
          border-radius: 1rem;
          padding: 2rem;
          text-align: center;
          transition: all 0.3s ease;
          border-bottom: 4px solid transparent;
        }
        
        .benefit-item:hover {
          border-bottom-color: #4ade80;
          transform: translateY(-5px);
          background: rgba(15, 23, 42, 0.7);
        }
        
        .benefit-value {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(90deg, #4ade80 0%, #22d3ee 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 0.5rem;
        }
        
        .benefit-title {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          font-weight: 600;
        }
        
        .benefit-desc {
          color: #94a3b8;
          font-size: 0.95rem;
          line-height: 1.6;
        }
        
        /* CTA Section */
        .cta-section {
          padding: 6rem 2rem;
          text-align: center;
        }
        
        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem;
          }
          
          .hero-subtitle {
            font-size: 1.1rem;
          }
          
          .section-title {
            font-size: 2rem;
          }
          
          section {
            padding: 4rem 1.5rem;
          }
          
          .solution-card, .tech-item, .benefit-item {
            padding: 1.75rem;
          }
        }
        
        @media (max-width: 480px) {
          .hero-title {
            font-size: 2rem;
          }
          
          .hero-section {
            padding: 4rem 1rem 6rem;
          }
          
          .section-title {
            font-size: 1.75rem;
          }
          
          .benefit-value {
            font-size: 2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AgriTechAIPage;